import { ApiResponseError } from '@app/utils/api';

export interface RunbookTemplateAuthoring {
  title?: string;
  instructions?: string;
  source_type?: string;
  source_hash?: string;
  source_uri?: string;
}

export interface TemplateDiagnosticsSummary {
  total_count?: number;
  warning_count?: number;
  error_count?: number;
}

export interface TemplateParameterSummary {
  total_count?: number;
  required_count?: number;
  resource_count?: number;
}

export interface TemplateHealthSummary {
  template_health?: string;
  warning_count?: number;
  error_count?: number;
  required_parameter_count?: number;
  supported_execution_readiness?: string;
  publish_state?: string;
}

export interface TemplateParameter {
  name: string;
  kind?: string;
  resource_kind?: string;
  shape?: string;
  required?: boolean;
  description?: string;
  aliases?: string[];
  selector_hints?: string[];
  default_value?: unknown;
  allowed_values?: string[];
}

export interface TemplateInput {
  name: string;
  kind?: string;
  ref?: string;
  shape?: string;
  required?: boolean;
}

export interface TemplateOutput {
  name: string;
  shape?: string;
  description?: string;
}

export interface TemplateStep {
  id: string;
  kind?: string;
  title?: string;
  description?: string;
  goal?: string;
  capability_ref?: string;
  depends_on?: string[];
  condition?: string;
  failure_policy?: string;
  failure_branch_to?: string;
  interface_family_hint?: string;
  decision?: Record<string, unknown>;
  loop?: Record<string, unknown>;
  approval?: Record<string, unknown>;
  inputs?: TemplateInput[];
  outputs?: TemplateOutput[];
}

export interface StoredTemplateDiagnostic {
  id?: string;
  runbook_version_id?: string;
  severity?: string;
  code?: string;
  message?: string;
  suggested_fix?: string;
  clarifying_question?: string;
  location?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface CompileValidationIssue {
  code?: string;
  stage?: string;
  message?: string;
  step_id?: string;
  path?: string;
  repairable?: boolean;
  severity?: string;
}

export interface CompileReviewReason {
  code?: string;
  category?: string;
  stage?: string;
  message?: string;
  issue_count?: number;
  repairable?: boolean;
  primary_issue?: CompileValidationIssue;
}

export interface TemplateSource {
  source_type?: string;
  source_hash?: string;
  source_uri?: string;
  source_revision?: string;
  source_text?: string;
  provenance?: string[];
  metadata?: Record<string, unknown>;
}

export interface RunbookTemplateSummary {
  runbook_id?: string;
  runbook_version_id?: string;
  runbook_name?: string;
  description?: string;
  source_type?: string;
  source_uri?: string;
  status?: string;
  version?: string;
  created_at?: string;
  updated_at?: string;
  compiled_at?: string | null;
  latest_version_id?: string;
  latest_version?: string;
  published_version?: string;
  published_version_id?: string;
  is_latest_version?: boolean;
  is_published_version?: boolean;
  diagnostics_summary?: TemplateDiagnosticsSummary;
  parameter_summary?: TemplateParameterSummary;
  summary?: TemplateHealthSummary;
  authoring?: RunbookTemplateAuthoring;
}

export interface RunbookTemplateVersionSummary extends RunbookTemplateSummary {}

export interface RunbookTemplateVersionDetail {
  summary?: RunbookTemplateVersionSummary;
  authoring?: RunbookTemplateAuthoring;
  source?: TemplateSource;
  parameters?: TemplateParameter[];
  steps?: TemplateStep[];
  diagnostics?: StoredTemplateDiagnostic[];
  compile_artifacts_summary?: {
    has_brief?: boolean;
    has_workflow_spec?: boolean;
    workflow_step_count?: number;
    variable_count?: number;
    condition_count?: number;
    edge_count?: number;
  };
  template_ir?: Record<string, unknown>;
}

export interface RunbookTemplateDetail {
  summary?: RunbookTemplateSummary;
  authoring?: RunbookTemplateAuthoring;
  active_version?: RunbookTemplateVersionDetail;
  versions?: RunbookTemplateVersionSummary[];
}

export interface CompileTemplateResult {
  status: string;
  runbook_id?: string;
  runbook_version_id?: string;
  template_status?: string;
  template_ir?: {
    parameters?: TemplateParameter[];
    steps?: TemplateStep[];
  } & Record<string, unknown>;
  diagnostics?: {
    review_reason?: CompileReviewReason;
    validation_errors?: CompileValidationIssue[];
  };
  missing_input_details?: Array<{ name?: string; message?: string; choices?: string[]; description?: string }>;
  assumptions?: string[];
  normalized_summary?: string;
}

export interface RunbookCompileAcceptedResponse {
  compilation_id: string;
  status: string;
  events_href: string;
  result_href: string;
}

export interface RunbookCompileStatusResponse {
  compilation_id: string;
  org_id: string;
  status: string;
  current_stage?: string;
  percent?: number;
  error?: string;
  attempt_id?: string;
  created_at?: string;
  updated_at?: string;
  result?: CompileTemplateResult;
}

export interface RunbookCompileEvent {
  seq: number;
  compilationId: string;
  status?: string;
  stage?: string;
  percent?: number;
  message?: string;
  attemptId?: string;
  updatedAt?: string;
  result?: CompileTemplateResult;
}

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

const resolvePublicApiBase = (basePath: string): string => basePath.replace(/\/+$/, '');

const runbooksUrl = (basePath: string, orgId: string, path: string): string =>
  `${resolvePublicApiBase(basePath)}/orgs/${encodeURIComponent(orgId)}/runbooks${path.startsWith('/') ? path : `/${path}`}`;

const expectJSON = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    throw new ApiResponseError(response, `Request failed with status ${response.status}`);
  }
  const contentType = response.headers.get('Content-Type') ?? '';
  if (!contentType.toLowerCase().includes('application/json')) {
    throw new ApiResponseError(response, 'Expected JSON response but received a different content type.');
  }
  return (await response.json()) as T;
};

export const listRunbookTemplates = async (authFetch: AuthFetch, basePath: string, orgId: string): Promise<RunbookTemplateSummary[]> => {
  const url = new URL(runbooksUrl(basePath, orgId, '/templates'));
  const response = await authFetch(url.toString(), { method: 'GET', headers: { Accept: 'application/json' } });
  const payload = await expectJSON<{ items?: RunbookTemplateSummary[] }>(response);
  return payload.items ?? [];
};

export const getRunbookTemplate = async (
  authFetch: AuthFetch,
  basePath: string,
  orgId: string,
  runbookId: string
): Promise<RunbookTemplateDetail> => {
  const url = new URL(runbooksUrl(basePath, orgId, `/templates/${encodeURIComponent(runbookId)}`));
  const response = await authFetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' }
  });
  const payload = await expectJSON<{ item: RunbookTemplateDetail }>(response);
  return payload.item;
};

export const getRunbookTemplateVersion = async (
  authFetch: AuthFetch,
  basePath: string,
  orgId: string,
  runbookVersionId: string
): Promise<RunbookTemplateVersionDetail> => {
  const url = new URL(runbooksUrl(basePath, orgId, `/templates/versions/${encodeURIComponent(runbookVersionId)}`));
  const response = await authFetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' }
  });
  const payload = await expectJSON<{ item: RunbookTemplateVersionDetail }>(response);
  return payload.item;
};

export const compileRunbookFromText = async (
  authFetch: AuthFetch,
  basePath: string,
  payload: { orgId: string; runbookTitle: string; sourceText: string }
): Promise<RunbookCompileAcceptedResponse> => {
  const response = await authFetch(runbooksUrl(basePath, payload.orgId, '/templates/compile'), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      org_id: payload.orgId,
      input_mode: 'form',
      runbook_title: payload.runbookTitle,
      source_type: 'text',
      source_text: payload.sourceText
    })
  });
  return expectJSON<RunbookCompileAcceptedResponse>(response);
};

export const getRunbookCompilation = async (
  authFetch: AuthFetch,
  basePath: string,
  orgId: string,
  compilationId: string
): Promise<RunbookCompileStatusResponse> => {
  const url = new URL(runbooksUrl(basePath, orgId, `/templates/compilations/${encodeURIComponent(compilationId)}`));
  const response = await authFetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' }
  });
  return expectJSON<RunbookCompileStatusResponse>(response);
};

export const getRunbookCompilationEventsUrl = (basePath: string, orgId: string, compilationId: string): string =>
  runbooksUrl(basePath, orgId, `/templates/compilations/${encodeURIComponent(compilationId)}/events`);

export const publishRunbookVersion = async (
  authFetch: AuthFetch,
  basePath: string,
  orgId: string,
  runbookVersionId: string
): Promise<RunbookTemplateVersionDetail> => {
  const url = new URL(runbooksUrl(basePath, orgId, `/templates/versions/${encodeURIComponent(runbookVersionId)}`));
  const response = await authFetch(url.toString(), {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ status: 'published' })
  });
  const payload = await expectJSON<{ item: RunbookTemplateVersionDetail }>(response);
  return payload.item;
};
